// 性能监控服务 - 实时监控应用性能指标
class PerformanceMonitor {
  constructor() {
    this.metrics = {
      // 页面加载性能
      pageLoad: {
        domContentLoaded: 0,
        loadComplete: 0,
        firstContentfulPaint: 0,
        largestContentfulPaint: 0,
        firstInputDelay: 0,
        cumulativeLayoutShift: 0
      },
      
      // 资源加载性能
      resources: {
        totalRequests: 0,
        failedRequests: 0,
        totalSize: 0,
        loadTime: 0,
        cacheHitRate: 0
      },
      
      // 运行时性能
      runtime: {
        memoryUsage: 0,
        memoryLimit: 0,
        cpuUsage: 0,
        frameRate: 0,
        longTasks: 0
      },
      
      // 用户交互性能
      interaction: {
        clickResponseTime: 0,
        scrollPerformance: 0,
        inputLatency: 0,
        navigationTime: 0
      },
      
      // 错误统计
      errors: {
        totalErrors: 0,
        jsErrors: 0,
        networkErrors: 0,
        resourceErrors: 0
      }
    }
    
    this.observers = new Map()
    this.isMonitoring = false
    this.reportInterval = 30000 // 30秒报告一次
    this.thresholds = {
      memory: 80, // 内存使用率阈值 80%
      frameRate: 30, // 帧率阈值 30fps
      responseTime: 100, // 响应时间阈值 100ms
      errorRate: 5 // 错误率阈值 5%
    }
    
    this.init()
  }
  
  // 初始化性能监控
  init() {
    if (this.isMonitoring) return
    
    this.isMonitoring = true
    this.setupPerformanceObservers()
    this.setupMemoryMonitoring()
    this.setupErrorMonitoring()
    this.setupInteractionMonitoring()
    this.startReporting()
    
    // console.log('✅ 性能监控系统已启动')
  }
  
  // 设置性能观察器
  setupPerformanceObservers() {
    // 页面加载性能
    if ('PerformanceObserver' in window) {
      // 监控 LCP (Largest Contentful Paint)
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          this.metrics.pageLoad.largestContentfulPaint = lastEntry.startTime
        })
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
        this.observers.set('lcp', lcpObserver)
      } catch (error) {
        console.warn('LCP 监控不可用:', error)
      }
      
      // 监控 FID (First Input Delay)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          entries.forEach(entry => {
            this.metrics.pageLoad.firstInputDelay = entry.processingStart - entry.startTime
          })
        })
        fidObserver.observe({ entryTypes: ['first-input'] })
        this.observers.set('fid', fidObserver)
      } catch (error) {
        console.warn('FID 监控不可用:', error)
      }
      
      // 监控 CLS (Cumulative Layout Shift)
      try {
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0
          const entries = list.getEntries()
          entries.forEach(entry => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value
            }
          })
          this.metrics.pageLoad.cumulativeLayoutShift = clsValue
        })
        clsObserver.observe({ entryTypes: ['layout-shift'] })
        this.observers.set('cls', clsObserver)
      } catch (error) {
        console.warn('CLS 监控不可用:', error)
      }
      
      // 监控长任务
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          this.metrics.runtime.longTasks += entries.length
        })
        longTaskObserver.observe({ entryTypes: ['longtask'] })
        this.observers.set('longtask', longTaskObserver)
      } catch (error) {
        console.warn('长任务监控不可用:', error)
      }
    }
    
    // 监控资源加载
    this.setupResourceMonitoring()
  }
  
  // 设置资源监控
  setupResourceMonitoring() {
    const originalFetch = window.fetch
    const originalXHR = window.XMLHttpRequest
    
    // 监控 fetch 请求
    window.fetch = async (...args) => {
      const startTime = performance.now()
      this.metrics.resources.totalRequests++
      
      try {
        const response = await originalFetch(...args)
        const endTime = performance.now()
        
        this.metrics.resources.loadTime += (endTime - startTime)
        
        if (!response.ok) {
          this.metrics.resources.failedRequests++
          this.metrics.errors.networkErrors++
        }
        
        return response
      } catch (error) {
        this.metrics.resources.failedRequests++
        this.metrics.errors.networkErrors++
        throw error
      }
    }
    
    // 监控 XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open
    const originalSend = XMLHttpRequest.prototype.send
    
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      this._startTime = performance.now()
      this._url = url
      return originalOpen.call(this, method, url, ...args)
    }
    
    XMLHttpRequest.prototype.send = function(...args) {
      this.metrics.resources.totalRequests++
      
      this.addEventListener('loadend', () => {
        const endTime = performance.now()
        this.metrics.resources.loadTime += (endTime - this._startTime)
        
        if (this.status >= 400) {
          this.metrics.resources.failedRequests++
          this.metrics.errors.networkErrors++
        }
      })
      
      return originalSend.call(this, ...args)
    }
  }
  
  // 设置内存监控
  setupMemoryMonitoring() {
    if ('memory' in performance) {
      setInterval(() => {
        const memory = performance.memory
        this.metrics.runtime.memoryUsage = memory.usedJSHeapSize
        this.metrics.runtime.memoryLimit = memory.jsHeapSizeLimit
        
        // 检查内存使用率
        const memoryUsagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
        if (memoryUsagePercent > this.thresholds.memory) {
          this.handleMemoryWarning(memoryUsagePercent)
        }
      }, 5000) // 每5秒检查一次
    }
  }
  
  // 设置错误监控
  setupErrorMonitoring() {
    // JavaScript 错误
    window.addEventListener('error', (event) => {
      this.metrics.errors.totalErrors++
      this.metrics.errors.jsErrors++
      this.handleError('JavaScript Error', event.error)
    })
    
    // Promise 拒绝
    window.addEventListener('unhandledrejection', (event) => {
      this.metrics.errors.totalErrors++
      this.metrics.errors.jsErrors++
      this.handleError('Unhandled Promise Rejection', event.reason)
    })
    
    // 资源加载错误
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target
        
        // 忽略已经被组件处理的图片错误
        // 检查是否是图片元素，且已经被处理（src 为空或 display 为 none）
        if (target.tagName === 'IMG') {
          if (!target.src || target.style.display === 'none') {
            return // 静默忽略已处理的错误
          }
          
          // 对于 403 错误（URL 过期），静默处理，不记录
          const src = target.src || ''
          if (
            src.includes('ark-content-generation') ||
            src.includes('volces.com') ||
            src.includes('volcengine.com') ||
            src.includes('doubao.com')
          ) {
            // 这些通常是 URL 过期导致的 403 错误，组件会显示占位符
            // 不需要记录到性能监控中
            return
          }
        }
        
        this.metrics.errors.totalErrors++
        this.metrics.errors.resourceErrors++
        this.handleError('Resource Error', target.src || target.href)
      }
    }, true)
  }
  
  // 设置交互监控
  setupInteractionMonitoring() {
    let clickStartTime = 0
    
    // 监控点击响应时间
    document.addEventListener('click', (event) => {
      clickStartTime = performance.now()
    })
    
    document.addEventListener('click', (event) => {
      const responseTime = performance.now() - clickStartTime
      this.metrics.interaction.clickResponseTime = responseTime
      
      if (responseTime > this.thresholds.responseTime) {
        this.handleSlowInteraction('Click', responseTime)
      }
    })
    
    // 监控滚动性能
    let scrollStartTime = 0
    let frameCount = 0
    
    document.addEventListener('scroll', () => {
      if (scrollStartTime === 0) {
        scrollStartTime = performance.now()
        frameCount = 0
      }
      
      frameCount++
      
      requestAnimationFrame(() => {
        const scrollTime = performance.now() - scrollStartTime
        if (scrollTime > 0) {
          const frameRate = (frameCount / scrollTime) * 1000
          this.metrics.runtime.frameRate = frameRate
          
          if (frameRate < this.thresholds.frameRate) {
            this.handleSlowInteraction('Scroll', frameRate)
          }
        }
      })
    })
  }
  
  // 开始性能报告
  startReporting() {
    setInterval(() => {
      this.generateReport()
    }, this.reportInterval)
  }
  
  // 生成性能报告
  generateReport() {
    const report = {
      timestamp: Date.now(),
      metrics: { ...this.metrics },
      summary: this.generateSummary()
    }
    
    // 检查性能问题
    this.checkPerformanceIssues(report)
    
    // 发送报告（可以发送到分析服务）
    this.sendReport(report)
    
    return report
  }
  
  // 生成性能摘要
  generateSummary() {
    const { pageLoad, resources, runtime, errors } = this.metrics
    
    return {
      // 页面加载性能评分
      pageLoadScore: this.calculatePageLoadScore(),
      
      // 资源加载性能评分
      resourceScore: this.calculateResourceScore(),
      
      // 运行时性能评分
      runtimeScore: this.calculateRuntimeScore(),
      
      // 错误率
      errorRate: (errors.totalErrors / resources.totalRequests) * 100,
      
      // 整体性能评分
      overallScore: this.calculateOverallScore(),
      
      // 建议
      recommendations: this.generateRecommendations()
    }
  }
  
  // 计算页面加载性能评分
  calculatePageLoadScore() {
    const { largestContentfulPaint, firstInputDelay, cumulativeLayoutShift } = this.metrics.pageLoad
    
    let score = 100
    
    // LCP 评分 (权重 40%)
    if (largestContentfulPaint > 4000) score -= 40
    else if (largestContentfulPaint > 2500) score -= 20
    else if (largestContentfulPaint > 1000) score -= 10
    
    // FID 评分 (权重 30%)
    if (firstInputDelay > 300) score -= 30
    else if (firstInputDelay > 100) score -= 15
    else if (firstInputDelay > 50) score -= 5
    
    // CLS 评分 (权重 30%)
    if (cumulativeLayoutShift > 0.25) score -= 30
    else if (cumulativeLayoutShift > 0.1) score -= 15
    else if (cumulativeLayoutShift > 0.05) score -= 5
    
    return Math.max(0, score)
  }
  
  // 计算资源加载性能评分
  calculateResourceScore() {
    const { totalRequests, failedRequests, loadTime } = this.metrics.resources
    
    if (totalRequests === 0) return 100
    
    let score = 100
    
    // 失败率评分
    const failureRate = (failedRequests / totalRequests) * 100
    score -= failureRate * 2
    
    // 平均加载时间评分
    const avgLoadTime = loadTime / totalRequests
    if (avgLoadTime > 2000) score -= 30
    else if (avgLoadTime > 1000) score -= 15
    else if (avgLoadTime > 500) score -= 5
    
    return Math.max(0, score)
  }
  
  // 计算运行时性能评分
  calculateRuntimeScore() {
    const { memoryUsage, memoryLimit, frameRate, longTasks } = this.metrics.runtime
    
    let score = 100
    
    // 内存使用率评分
    if (memoryLimit > 0) {
      const memoryUsagePercent = (memoryUsage / memoryLimit) * 100
      if (memoryUsagePercent > 90) score -= 40
      else if (memoryUsagePercent > 80) score -= 20
      else if (memoryUsagePercent > 70) score -= 10
    }
    
    // 帧率评分
    if (frameRate > 0) {
      if (frameRate < 30) score -= 30
      else if (frameRate < 45) score -= 15
      else if (frameRate < 55) score -= 5
    }
    
    // 长任务评分
    score -= longTasks * 2
    
    return Math.max(0, score)
  }
  
  // 计算整体性能评分
  calculateOverallScore() {
    const pageLoadScore = this.calculatePageLoadScore()
    const resourceScore = this.calculateResourceScore()
    const runtimeScore = this.calculateRuntimeScore()
    
    return Math.round((pageLoadScore + resourceScore + runtimeScore) / 3)
  }
  
  // 生成性能建议
  generateRecommendations() {
    const recommendations = []
    const { pageLoad, resources, runtime, errors } = this.metrics
    
    // 页面加载建议
    if (pageLoad.largestContentfulPaint > 2500) {
      recommendations.push('优化 LCP：考虑使用图片懒加载、代码分割或 CDN')
    }
    
    if (pageLoad.firstInputDelay > 100) {
      recommendations.push('优化 FID：减少主线程阻塞，使用 Web Workers')
    }
    
    if (pageLoad.cumulativeLayoutShift > 0.1) {
      recommendations.push('优化 CLS：为图片和广告设置尺寸，避免动态内容插入')
    }
    
    // 资源加载建议
    if (resources.failedRequests > 0) {
      recommendations.push('检查网络请求：修复失败的请求，添加重试机制')
    }
    
    if (resources.totalRequests > 50) {
      recommendations.push('减少请求数量：合并资源、使用雪碧图、内联小资源')
    }
    
    // 运行时建议
    if (runtime.memoryLimit > 0) {
      const memoryUsagePercent = (runtime.memoryUsage / runtime.memoryLimit) * 100
      if (memoryUsagePercent > 80) {
        recommendations.push('优化内存使用：检查内存泄漏，及时清理不需要的对象')
      }
    }
    
    if (runtime.frameRate > 0 && runtime.frameRate < 45) {
      recommendations.push('优化渲染性能：减少重绘重排，使用 transform 和 opacity')
    }
    
    if (runtime.longTasks > 5) {
      recommendations.push('优化长任务：将耗时操作移到 Web Workers 或分片执行')
    }
    
    // 错误建议
    if (errors.totalErrors > 0) {
      recommendations.push('修复错误：检查控制台错误，添加错误边界')
    }
    
    return recommendations
  }
  
  // 检查性能问题
  checkPerformanceIssues(report) {
    const { summary } = report
    
    if (summary.overallScore < 70) {
      this.handlePerformanceWarning('整体性能较差', summary.overallScore)
    }
    
    if (summary.errorRate > this.thresholds.errorRate) {
      this.handlePerformanceWarning('错误率过高', summary.errorRate)
    }
  }
  
  // 处理内存警告
  handleMemoryWarning(usagePercent) {
    console.warn(`⚠️ 内存使用率过高: ${usagePercent.toFixed(2)}%`)
    
    // 触发垃圾回收
    if (window.gc) {
      window.gc()
    }
    
    // 发送内存警告事件
    this.dispatchEvent('memoryWarning', { usagePercent })
  }
  
  // 处理错误
  handleError(type, error) {
    console.error(`❌ ${type}:`, error)
    
    // 发送错误事件
    this.dispatchEvent('error', { type, error })
  }
  
  // 处理慢交互
  handleSlowInteraction(type, value) {
    // console.warn(`⚠️ ${type} 性能较差:`, value)
    
    // 发送慢交互事件
    this.dispatchEvent('slowInteraction', { type, value })
  }
  
  // 处理性能警告
  handlePerformanceWarning(type, value) {
    // console.warn(`⚠️ 性能警告 - ${type}:`, value)
    
    // 发送性能警告事件
    this.dispatchEvent('performanceWarning', { type, value })
  }
  
  // 发送报告
  sendReport(report) {
    // 这里可以发送到分析服务
    // 例如：Google Analytics, 自定义分析服务等
    // console.log('📊 性能报告:', report)
  }
  
  // 分发事件
  dispatchEvent(eventType, data) {
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(`performance:${eventType}`, { 
        detail: data 
      }))
    }
  }
  
  // 获取当前指标
  getMetrics() {
    return { ...this.metrics }
  }
  
  // 获取性能摘要
  getSummary() {
    return this.generateSummary()
  }
  
  // 重置指标
  resetMetrics() {
    this.metrics = {
      pageLoad: {
        domContentLoaded: 0,
        loadComplete: 0,
        firstContentfulPaint: 0,
        largestContentfulPaint: 0,
        firstInputDelay: 0,
        cumulativeLayoutShift: 0
      },
      resources: {
        totalRequests: 0,
        failedRequests: 0,
        totalSize: 0,
        loadTime: 0,
        cacheHitRate: 0
      },
      runtime: {
        memoryUsage: 0,
        memoryLimit: 0,
        cpuUsage: 0,
        frameRate: 0,
        longTasks: 0
      },
      interaction: {
        clickResponseTime: 0,
        scrollPerformance: 0,
        inputLatency: 0,
        navigationTime: 0
      },
      errors: {
        totalErrors: 0,
        jsErrors: 0,
        networkErrors: 0,
        resourceErrors: 0
      }
    }
  }
  
  // 停止监控
  stop() {
    this.isMonitoring = false
    
    // 清理观察器
    this.observers.forEach(observer => observer.disconnect())
    this.observers.clear()
    
    // console.log('⏹️ 性能监控系统已停止')
  }
  
  // 销毁监控器
  destroy() {
    this.stop()
    this.resetMetrics()
  }
}

// 创建单例实例
const performanceMonitor = new PerformanceMonitor()

export default performanceMonitor

